import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ClinicalImpression } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Button, Card, Flex } from "@mantine/core";

import {
  ClientNote,
  ClientNoteStatus,
  updateClientNoteStatus,
} from "@/clients/zod-schemas/client-note";
import { TextWrapper } from "@/components/TextWrapper";
import { getClinicalImpressionData } from "@/fhir/resource-helpers/clinical-impression";
import PermissionGate from "@/permission/components/PermissionGate";
import { WRITE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usDateTimeFormat } from "@/utils/datetime";

import { ArchiveModalProps } from "../ClientRemindersNote/ArchiveClientReminderNote";
import { useClientNotes } from "../utils/hooks";

import EditClientConfidentialNote from "./EditClientConfidentialNote";
import EditSignature from "./EditSignature";

interface DisplayClientConfidentialNoteProps extends ArchiveModalProps {
  setBackground: (color: string | undefined) => void;
}

export default function DisplayClientConfidentialNote({
  setBackground,
  refresh,
  setRefresh,
}: DisplayClientConfidentialNoteProps) {
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [filingNoteId, setFilingNoteId] = useState<string | null>(null);
  const medplum = useMedplum();
  const { clientId } = useParams() as { clientId: string };

  const confidentialNotes = useClientNotes(
    clientId,
    medplum,
    ClientNoteStatus.InProgress,
    refresh,
    ClientNote.Confidential,
  );

  const background = confidentialNotes.length > 0 ? "yellow.1" : "gray.2";

  useEffect(() => {
    setBackground(background);
  }, [confidentialNotes, background, setBackground]);

  const handleFileConfidentialNote = useCallback(
    async (confidentialNote: ClinicalImpression) => {
      if (filingNoteId) return;
      setFilingNoteId(confidentialNote.id || null);
      try {
        await updateClientNoteStatus({
          medplum,
          clinicalImpression: confidentialNote,
          status: ClientNoteStatus.Completed,
        });
        setRefresh((prev) => !prev);
      } finally {
        setFilingNoteId(null);
      }
    },
    [medplum, setRefresh, filingNoteId],
  );

  return confidentialNotes.map((confidentialNote, index) => {
    const { assessorDisplay, date, summary, lastUpdated } =
      getClinicalImpressionData(confidentialNote);
    const creationDate = usDateTimeFormat(date);
    const isEditing = editingNoteId === confidentialNote.id;

    // Check if the note has been edited (lastUpdated is different from creation date)
    // Allow for small differences in timestamps but require at least 5 seconds difference
    const hasBeenEdited =
      lastUpdated &&
      date &&
      lastUpdated !== date &&
      Math.abs(new Date(lastUpdated).getTime() - new Date(date).getTime()) > 5000; // More than 5 seconds difference

    if (isEditing) {
      return (
        <EditClientConfidentialNote
          setRefresh={setRefresh}
          background={background}
          key={confidentialNote.id}
          clinicalImpression={confidentialNote}
          handleToggleEdit={() => setEditingNoteId(null)}
        />
      );
    }

    return (
      <Card pt="lg" bg={background} key={confidentialNote.id}>
        <TextWrapper mb="xs" size="sm" data-testid="confidential-note-text">
          {summary}
        </TextWrapper>
        <TextWrapper mb="xs" size="xs" fs="italic">
          {assessorDisplay} {creationDate}
        </TextWrapper>
        {hasBeenEdited && <EditSignature clinicalImpression={confidentialNote} />}
        <PermissionGate allowPatient={false} requiredPermissions={WRITE_PERMISSION_FLAGS}>
          <Flex gap="xs" align="center" justify="flex-end">
            <Button
              color="black"
              variant="subtle"
              size="compact-xs"
              data-testid="edit-confidential-note-button"
              onClick={() => setEditingNoteId(confidentialNote.id || null)}
            >
              Edit
            </Button>
            <Button
              color="black"
              variant="outline"
              size="compact-xs"
              disabled={filingNoteId !== null}
              data-testid="file-confidential-note-button"
              loading={filingNoteId === confidentialNote.id}
              onClick={() => handleFileConfidentialNote(confidentialNote)}
            >
              File
            </Button>
          </Flex>
        </PermissionGate>
      </Card>
    );
  });
}
